import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { TagsInput } from "@/components/TagsInput";
import { PriceToggle } from "@/components/PriceToggle";
import { HighlightSelect } from "@/components/HighlightSelect";

const inputCls = "bg-[#16161D] border border-white/10 rounded-lg px-2 py-1.5 text-white text-xs w-full";
const detect = (title) => (/mashup/i.test(title) ? "mashup" : /remix/i.test(title) ? "remix" : "pool");

export function PackBulkPanel({ pack, selected, tracks, onApplied, onClear }) {
  const [advanced, setAdvanced] = useState(false);
  const [quickGenre, setQuickGenre] = useState("");
  const [f, setF] = useState({ publish: "keep", category: "", genre: "", bpm: "", tags: [], price: "", is_featured: false, is_top: false, is_highlighted: false, is_new: false, highlight_genres: [], sections: [], setGenre: false, setTags: false, setHighlight: false, setPrice: false });
  const [busy, setBusy] = useState(false);
  const n = selected.length;

  const apply = async () => {
    const body = { ids: selected, action: "apply" };
    if (f.publish !== "keep") body.publish = f.publish === "yes";
    if (f.category) body.category = f.category;
    if (f.setGenre) body.genre = f.genre;
    if (f.bpm) body.bpm = Number(f.bpm);
    if (f.setTags) body.tags = f.tags.map((x) => x.trim()).filter(Boolean);
    if (f.setPrice && f.price !== "") body.price = Number(f.price) || 0;
    if (f.setHighlight) { body.is_featured = f.is_featured; body.is_top = f.is_top; body.is_highlighted = f.is_highlighted; body.is_new = f.is_new; body.highlight_genres = f.highlight_genres; body.sections = f.sections; }
    setBusy(true);
    try {
      const res = await api.post(`/admin/packs/${pack.id}/tracks/bulk`, body);
      onApplied(res.data);
      toast.success(`Cambios aplicados a ${n} pista${n === 1 ? "" : "s"}`);
      onClear();
    } catch { toast.error("No se pudieron aplicar los cambios"); } finally { setBusy(false); }
  };

  const quick = async (body) => {
    setBusy(true);
    try {
      const res = await api.post(`/admin/packs/${pack.id}/tracks/bulk`, { ids: selected, action: "apply", ...body });
      onApplied(res.data);
      toast.success(`Aplicado a ${n} pista${n === 1 ? "" : "s"}`);
    } catch { toast.error("No se pudo aplicar"); } finally { setBusy(false); }
  };

  const del = async () => {
    if (!window.confirm(`¿Eliminar ${n} pistas del pack?`)) return;
    setBusy(true);
    try {
      const res = await api.post(`/admin/packs/${pack.id}/tracks/bulk`, { ids: selected, action: "delete" });
      onApplied(res.data); toast.success("Pistas eliminadas"); onClear();
    } catch { toast.error("Error al eliminar"); } finally { setBusy(false); }
  };

  const split = async () => {
    setBusy(true);
    try {
      const res = await api.post(`/admin/packs/${pack.id}/tracks/bulk`, { ids: selected, action: "split" });
      onApplied(res.data); toast.success("Artista y título separados");
    } catch { toast.error("Error"); } finally { setBusy(false); }
  };

  const Toggle = ({ k, label }) => (
    <label className="flex items-center gap-1.5 text-[11px] text-white/60 cursor-pointer">
      <input type="checkbox" checked={!!f[k]} onChange={(e) => setF({ ...f, [k]: e.target.checked })} className="accent-red-600" data-testid={`pack-bulk-${k}`} /> {label}
    </label>
  );

  const autoCats = [...new Set(tracks.filter((t) => selected.includes(t.id)).map((t) => t.category || detect(t.title)))];
  const selGenres = [...new Set([...(f.setGenre ? f.genre : "").split(","), ...tracks.filter((t) => selected.includes(t.id)).flatMap((t) => (t.genre || "").split(","))].map((g) => g.trim()).filter(Boolean))];

  return (
    <div className="p-3 rounded-xl border border-red-900/40 bg-red-950/10 space-y-3" data-testid="pack-bulk-panel">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="font-mono text-[10px] uppercase tracking-widest text-red-400">Aplicar a {n} pista{n === 1 ? "" : "s"} seleccionada{n === 1 ? "" : "s"}</div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={split} disabled={busy} data-testid="edit-pack-bulk-split" className="text-[11px] font-semibold px-3 py-1.5 rounded-full bg-white/5 border border-white/10 hover:border-red-600/50 text-white/80">Separar "Artista - Título"</button>
          <button type="button" onClick={del} disabled={busy} data-testid="edit-pack-bulk-delete" className="text-[11px] font-semibold px-3 py-1.5 rounded-full bg-red-600/20 text-red-400 hover:bg-red-600/30">Eliminar</button>
        </div>
      </div>

      <form onSubmit={(e) => { e.preventDefault(); if (quickGenre.trim()) quick({ genre: quickGenre.trim() }); }} className="flex items-center gap-2" data-testid="pack-bulk-genre-form">
        <input value={quickGenre} onChange={(e) => setQuickGenre(e.target.value)} placeholder="Género(s) para la selección · Enter para aplicar" autoFocus data-testid="pack-bulk-genre-quick"
          className="flex-1 bg-[#16161D] border border-white/10 rounded-lg px-3 py-1.5 text-xs text-white outline-none focus:border-red-600/60" />
        <button type="submit" disabled={busy || !quickGenre.trim()} data-testid="pack-bulk-genre-quick-apply" className="btn-red text-white text-xs px-3 py-1.5 rounded-lg font-semibold disabled:opacity-40">Aplicar género</button>
      </form>
      <div className="grid grid-cols-3 gap-2" data-testid="pack-bulk-quick">
        {[
          ["Sección", "Publicar en su sección (Remix/Mashup por título, resto Pool)", { publish: true }, { publish: false }, "section"],
          ["Destacar", "Mostrar en grande dentro de su sección", { publish: true, is_highlighted: true }, { is_highlighted: false }, "highlight"],
          ["Novedad", "Mostrar en Novedades del Inicio", { publish: true, is_new: true }, { is_new: false }, "new"],
        ].map(([label, hint, onBody, offBody, key]) => (
          <div key={key} className="rounded-lg border border-white/10 bg-black/30 p-2 space-y-1.5" title={hint}>
            <div className="font-mono text-[10px] uppercase tracking-widest text-white/70 text-center">{label}</div>
            <div className="grid grid-cols-2 gap-1">
              <button type="button" disabled={busy} onClick={() => quick(onBody)} data-testid={`pack-bulk-${key}-on`}
                className="text-[11px] font-semibold py-1.5 rounded-md bg-red-600/20 text-red-300 hover:bg-red-600/35 disabled:opacity-40">Sí</button>
              <button type="button" disabled={busy} onClick={() => quick(offBody)} data-testid={`pack-bulk-${key}-off`}
                className="text-[11px] font-semibold py-1.5 rounded-md bg-white/5 text-white/60 hover:bg-white/10 disabled:opacity-40">No</button>
            </div>
          </div>
        ))}
      </div>

      <button type="button" onClick={() => setAdvanced(!advanced)} data-testid="pack-bulk-advanced-toggle"
        className="w-full text-[10px] font-mono uppercase tracking-widest text-white/40 hover:text-red-400 py-1">{advanced ? "▲ Ocultar más opciones" : "▼ Más opciones (sección fija, género, BPM, tags, precio…)"}</button>

      {advanced && (<>
      <div className="grid sm:grid-cols-2 gap-2">
        <label className="block">
          <span className="font-mono text-[9px] uppercase tracking-widest text-white/40 mb-1 block">Pista individual (sección)</span>
          <select className={inputCls} value={f.publish} onChange={(e) => setF({ ...f, publish: e.target.value })} data-testid="pack-bulk-publish">
            <option value="keep">— sin cambios —</option>
            <option value="yes">Publicar en sección</option>
            <option value="no">Quitar de la sección</option>
          </select>
        </label>
        <label className="block">
          <span className="font-mono text-[9px] uppercase tracking-widest text-white/40 mb-1 block">Sección</span>
          <select className={inputCls} value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} data-testid="pack-bulk-category">
            <option value="">Automática por título ({autoCats.join(", ") || "—"})</option>
            <option value="remix">Remix</option><option value="mashup">Mashup</option><option value="original">Original</option><option value="pool">Pool</option>
          </select>
        </label>
      </div>

      <div className="grid sm:grid-cols-2 gap-2">
        <div className="space-y-1">
          <Toggle k="setGenre" label="Cambiar género(s)" />
          <input className={inputCls} placeholder="Género(s), separados por coma" value={f.genre} disabled={!f.setGenre} onChange={(e) => setF({ ...f, genre: e.target.value })} data-testid="pack-bulk-genre" />
        </div>
        <div className="space-y-1">
          <span className="text-[11px] text-white/60 block">BPM (vacío = sin cambios)</span>
          <input className={inputCls} type="number" placeholder="BPM" value={f.bpm} onChange={(e) => setF({ ...f, bpm: e.target.value })} data-testid="pack-bulk-bpm" />
        </div>
      </div>

      <div className="space-y-1">
        <Toggle k="setTags" label="Cambiar tags" />
        <div className={f.setTags ? "" : "opacity-40 pointer-events-none"}><TagsInput small value={f.tags} onChange={(tags) => setF({ ...f, tags })} testid="pack-bulk-tag" /></div>
      </div>

      <div className="grid sm:grid-cols-2 gap-2">
        <div className="space-y-1">
          <Toggle k="setPrice" label="Cambiar precio (pista individual)" />
          <div className={f.setPrice ? "" : "opacity-40 pointer-events-none"}><PriceToggle small value={f.price || "0"} onChange={(price) => setF({ ...f, price })} testid="pack-bulk-price" /></div>
        </div>
        <div className="space-y-1">
          <Toggle k="setHighlight" label="Cambiar destacados" />
          <div className={f.setHighlight ? "" : "opacity-40 pointer-events-none"}>
            <HighlightSelect small value={{ is_featured: f.is_featured, is_top: f.is_top, is_highlighted: f.is_highlighted, is_new: f.is_new, highlight_genres: f.highlight_genres, sections: f.sections }} genres={selGenres} category={f.category || autoCats[0]} onChange={(v) => setF({ ...f, ...v })} testid="pack-bulk-highlight" />
          </div>
        </div>
      </div>

      <button type="button" onClick={apply} disabled={busy} data-testid="pack-bulk-apply"
        className="w-full btn-red text-white text-xs py-2.5 rounded-lg font-semibold disabled:opacity-50">
        {busy ? "Aplicando..." : `Aplicar a ${n} pista${n === 1 ? "" : "s"}`}
      </button>
      </>)}
    </div>
  );
}
